import React, { ReactNode, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { ReconnectContactAvatarList } from '@components/ContactAvatar';
import styles from './ActivityListItem.module.scss';
import Contact from '@repo/utils/models/Contact';
import { useAuthContext } from '@repo/hooks';
import {
  ActivityAnnouncementSubtype,
  ActivityItem,
  ActivityItemMetadata,
} from '@repo/utils/models/Activity';
import ContactLinkComponent from '@components/ContactLink';
import Occasion, { timelineDateDisplay } from '@repo/utils/helpers/Occasion';
import {
  BadgedAvatar,
  AvatarGroup,
  ClayBadge,
  ImportAvatars,
} from './ActivityListItemAvatars/ActivityListItemAvatars';
import { Actions } from './ItemActions/ItemActions';
import renderPostMarkdown from './Announcements/renderMarkdown';
import ConnectIntegrations from './Announcements/ConnectIntegrations';
import { Link } from 'react-router-dom';
import Group from '@repo/utils/models/Group';
import { ID } from '@repo/utils/types';
import useEnabledNotifications from '@views/Settings/panels/Notifications/use-enabled-notifications';
import { useDispatch } from 'react-redux';
import { dismissActivityItem } from '@repo/redux/actions/activity';
import { pluralize } from '@repo/utils/helpers/string';
import { openActivityItemInModal } from '@repo/redux/actions/ui';
import { SmallButton } from '@components/Button';
import { renderSocialDiffMarkdown } from '@repo/utils/helpers/markdown';
import { BadgeType } from './BadgeIcon/BadgeIcon';
import { useTheme } from '@repo/themes';

interface ActivityListItemComponentProps {
  item: ActivityItem;
  date: Occasion;
  selected: boolean;
  noContactActions?: boolean;
  noDismissal?: boolean;
  onlyRelativeDate?: boolean;
}

const ContactLink = ({ contact, children }: { contact: Contact; children: ReactNode }) => {
  return (
    <ContactLinkComponent contact={contact} className={styles.contactLink}>
      {children}
    </ContactLinkComponent>
  );
};

const DateDisplay = ({
  date,
  className,
  selected,
}: {
  date: Occasion;
  className?: string;
  selected?: boolean;
}) => (
  <span className={cn(styles.relativeDate, 'relative-date', className, selected && styles.hidden)}>
    {timelineDateDisplay(date)}
  </span>
);

const BirthdayActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const { user } = useAuthContext();
  const isSelf = String(user?.contact_id) === item?.metadata.contact.id;
  let badgeType: BadgeType = 'birthday';
  let actions = (
    <Actions
      noBg={noContactActions}
      contact={!noContactActions && !isSelf && item?.metadata.contact}
      activityItemID={item.id}
      noDismissal={noDismissal}
    />
  );
  let message: ReactNode = (
    <>
      Wish <b>{item?.metadata.contact.displayName}</b> a happy birthday!
    </>
  );

  if (isSelf) {
    message = 'Happy birthday, cheers to a bright year ahead!';
  }

  if (item?.metadata.contact.isMemorialized) {
    badgeType = 'memorial';
    actions = <Actions activityItemID={item.id} />;
    message = (
      <>
        <b>{item?.metadata.contact.displayName}'s</b> birthday today
      </>
    );
  }

  return (
    <>
      <BadgedAvatar contact={item?.metadata.contact} badgeType={badgeType} />
      <p>{message}</p>
      <DateDisplay date={date} selected={selected} />
      {selected && actions}
    </>
  );
};

const NewMemberActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => (
  <>
    <BadgedAvatar contact={item?.metadata.contact} badgeType="new-member" />
    <p>
      <b>{item?.metadata.contact.displayName}</b> joined Clay. Give them a warm welcome!
    </p>
    <DateDisplay date={date} selected={selected} />

    {selected && (
      <Actions
        noBg={noContactActions}
        contact={!noContactActions && item?.metadata.contact}
        activityItemID={item.id}
        noDismissal={noDismissal}
      />
    )}
  </>
);

const SharedContactLink = ({ contact }: { contact: Contact }) => (
  <Link to={`/share/contact/${contact.id}`} className={styles.contactLink}>
    {contact.displayName}
  </Link>
);

const SharedGroupLink = ({ group }: { group: Group }) => (
  <Link to={`/share/group/${group.id}`} className={styles.contactLink}>
    {group.title}
  </Link>
);

const SharedActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  let copy: ReactNode = '';
  if (item?.metadata.sharedContact) {
    copy = (
      <>
        {' '}
        shared the profile of <SharedContactLink contact={item?.metadata.sharedContact} /> with you.
      </>
    );
  } else if (item?.metadata.sharedGroup) {
    copy = (
      <>
        {' '}
        invited you to the group <SharedGroupLink group={item?.metadata.sharedGroup} />
      </>
    );
  }

  return (
    <>
      <BadgedAvatar contact={item?.metadata.sharedBy} badgeType="shared" />
      <p>
        <b>{item?.metadata.sharedBy.displayName}</b>
        {copy}
      </p>
      <DateDisplay date={date} selected={selected} />

      {selected && (
        <Actions
          noBg={noContactActions}
          contact={!noContactActions && item?.metadata.sharedBy}
          activityItemID={item.id}
          noDismissal={noDismissal}
        />
      )}
    </>
  );
};

const ContactListNames = ({
  contacts,
  total,
  remainderLabel = 'more people',
}: {
  contacts: Contact[];
  total: number;
  remainderLabel?: string;
}) => {
  const names = contacts.map((c, i) => (
    <ContactLink key={i} contact={c}>
      {c.firstName || c.displayName}
    </ContactLink>
  ));
  if (total === 1) {
    return names[0];
  } else if (total === 2) {
    return (
      <>
        {names[0]} and {names[1]}
      </>
    );
  } else if (total === 3) {
    return (
      <>
        {names[0]}, {names[1]}, and {names[2]}
      </>
    );
  } else {
    return (
      <>
        {names[0]}, {names[1]}, {names[2]} &amp; <b>{total - 3}</b> {remainderLabel}
      </>
    );
  }
};

const ImportDetails = ({
  importName,
  contacts,
  importTotal,
}: {
  importName: string;
  contacts: Contact[];
  importTotal: number;
}) => {
  const verb = importTotal === 1 ? 'has' : 'have';

  return (
    <>
      <ContactListNames contacts={contacts} total={importTotal} /> {verb} been added to your Clay
      via <span className={styles.source}>{importName}</span>
    </>
  );
};

const ImportActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const { importSource, importName, importTotal, contacts, groupId, groupName } = item.metadata;

  if (importSource === 'bulk_import') {
    let copy = (
      <>
        the group <Link to={`/group/${groupId}`}>{groupName}</Link>
      </>
    );
    if (!groupId || !groupName) {
      copy = <>Clay</>;
    }

    return (
      <>
        <ClayBadge />
        <p>Import complete! Your people have been added to {copy}</p>
        <DateDisplay date={date} selected={selected} />
        {selected && (
          <Actions noBg={noContactActions} activityItemID={item.id} noDismissal={noDismissal} />
        )}
      </>
    );
  }

  return (
    <>
      {importTotal === 1 ? (
        <>
          <BadgedAvatar contact={contacts[0]} badgeType={importSource} />
          <p>
            <ImportDetails importName={importName} contacts={contacts} importTotal={importTotal} />
          </p>
        </>
      ) : (
        <ImportAvatars contacts={contacts} importSource={importSource} importTotal={importTotal} />
      )}
      <DateDisplay date={date} selected={selected} />

      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={importTotal === 1 && !noContactActions && contacts[0]}
          noDismissal={noDismissal}
        />
      )}
      {importTotal > 1 && (
        <div className={styles.importDetails}>
          <ImportDetails importName={importName} contacts={contacts} importTotal={importTotal} />
        </div>
      )}
    </>
  );
};

const displayEventTimeSpan = (metadata: ActivityItemMetadata) => {
  if (metadata.startDate && metadata.endDate) {
    return ` • ${metadata.startDate.format('h:mm a')} - ${metadata.endDate.format('h:mm a')}`;
  } else if (metadata.startDate) {
    return ` • ${metadata.startDate.format('h:mm a')}`;
  }
  return '';
};

const EventActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const contacts = item?.metadata.contacts;
  const isForOneContact = contacts.length === 1;
  return (
    <>
      {isForOneContact ? (
        <BadgedAvatar contact={contacts[0]} badgeType="calendar" />
      ) : (
        <AvatarGroup contacts={contacts} badgeType="calendar" />
      )}
      <div className={cn(styles.nameAndDate, styles.eventNameAndDate, 'nameAndDate')}>
        {isForOneContact ? (
          <b className="contactName">{contacts[0].displayName}</b>
        ) : (
          <span className={styles.contactListNames}>
            <ContactListNames contacts={contacts} total={contacts.length} remainderLabel="more" />
          </span>
        )}
        <span className={cn(styles.date, 'information')}>
          {date.format('MMM d')}
          {date.isThisYear ? ' ' : date.format(' yyyy ')}
          {displayEventTimeSpan(item?.metadata)}
        </span>
      </div>
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && isForOneContact && contacts[0]}
          noDismissal={noDismissal}
        />
      )}
    </>
  );
};

const ReconnectDetails = ({ contacts }: { contacts: Contact[] }) => (
  <>
    Reflect on your relationship with{' '}
    <ContactListNames
      contacts={contacts}
      total={contacts.length}
      remainderLabel={contacts.length === 4 ? 'other' : 'others'}
    />
    .
  </>
);

const ReconnectActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const dispatch = useDispatch();
  const { enabledNotifications } = useEnabledNotifications();
  const multiplePeoplePerReconnect = enabledNotifications['activity_group_reconnects'];
  let contacts = item.filteredContacts;

  // If for some reason there are no "active" contacts on the reconnect item, dismiss the item itself
  useEffect(() => {
    if (contacts.length === 0) {
      dispatch(dismissActivityItem(item));
    }
  }, []);

  return multiplePeoplePerReconnect ? (
    <>
      <ReconnectContactAvatarList
        className={styles.reconnectContactAvatarList}
        contacts={contacts}
        contactIsDismissed={(contactID: ID) => item.contactStatus(contactID) === 'dismissed'}
      />
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={contacts.length === 1 && !noContactActions && contacts[0]}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
      <div className={styles.reconnectDetails}>
        <ReconnectDetails contacts={contacts} />
      </div>
    </>
  ) : (
    <>
      <BadgedAvatar contact={contacts[0]} badgeType="reconnect" />
      <p>
        <ReconnectDetails contacts={contacts} />
      </p>
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          contact={contacts.length === 1 && !noContactActions && contacts[0]}
          activityItemID={item.id}
          noDismissal={noDismissal}
        />
      )}
    </>
  );
};

const formatDate = (date: Occasion) => {
  let day = date.format('MMM d');

  if (date.isToday) {
    day = 'today';
  } else if (date.isYesterday) {
    day = 'yesterday';
  }

  return day;
};

const TitleAndDate = ({
  title,
  what,
  where,
  date,
  noDate,
}: {
  title: string;
  what?: string;
  where?: string;
  date: Occasion;
  noDate?: boolean;
}) => {
  const day = formatDate(date);
  const dateString = [what, !noDate && day, where].filter(Boolean).join(' • ');

  return (
    <div className={cn(styles.nameAndDate, 'nameAndDate')}>
      <b className="contactName">{title}</b>
      <span className={cn(styles.date, 'information')}>{dateString}</span>
    </div>
  );
};

const NewsLink = ({
  title,
  link,
  imageSrc,
  outlet,
  noVia,
  noCrop,
}: ActivityItemMetadata & { noVia?: boolean; noCrop?: boolean }) =>
  !!title &&
  !!link && (
    <a
      className={cn(styles.newsLink, 'newsLink')}
      href={link}
      target="_blank"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.copy}>
        <div className={styles.title}>{title}</div>
        <div className={cn(styles.source, 'information-item')}>
          {noVia ? '' : 'via '}
          {outlet}
        </div>
      </div>
      {imageSrc && !noCrop && (
        <div className={styles.image} style={{ backgroundImage: `url(${imageSrc})` }} />
      )}
      {imageSrc && noCrop && <img className={styles.noCropImage} src={imageSrc} />}
    </a>
  );

const NewsActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemComponentProps) => {
  const { contact, imageSrc, link, title, outlet } = item.metadata;
  return (
    <>
      <BadgedAvatar contact={contact} badgeType="news" />
      <TitleAndDate
        title={contact.displayName}
        what="In the news"
        date={date}
        noDate={onlyRelativeDate}
      />
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          activityItemID={item.id}
          contact={contact}
          pinToTop
          noBg={noContactActions}
          noDismissal={noDismissal}
        />
      )}
      <NewsLink title={title} link={link} imageSrc={imageSrc} outlet={outlet} />
    </>
  );
};

const ReminderActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const paddedDate = useMemo(() => new Occasion(date.addDays(1)), []);
  const day = formatDate(date);

  return (
    <>
      <BadgedAvatar contact={item?.metadata.contact} badgeType="reminder" date={paddedDate} />
      <div className={styles.reminderDetails}>
        You set a reminder for{' '}
        <ContactLink contact={item.contact}>{item.contact.displayName}</ContactLink> {day} at{' '}
        {date.format('h:mm a')}
      </div>
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && item?.metadata.contact}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
    </>
  );
};

const ExpandableContent = ({
  renderedContent,
  activityItemID,
}: {
  renderedContent: ReactNode;
  activityItemID: ID;
}) => {
  const dispatch = useDispatch();

  return (
    <div className={cn(styles.copyContainer, 'expandableCopyContainer')}>
      <div className={cn(styles.copy, styles.firstLine)}>{renderedContent}</div>
      <div className={cn(styles.copy, styles.secondLine)}>{renderedContent}</div>
      <SmallButton
        className={cn(styles.seeMoreBtn, 'seeMoreBtn')}
        onClick={(e) => {
          dispatch(openActivityItemInModal(activityItemID));
          e.stopPropagation();
        }}
      >
        Expand
      </SmallButton>
    </div>
  );
};

const DiffActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemComponentProps) => {
  const { contact, subtype, source } = item?.metadata || {};
  const expandableDiff = subtype === 'bio';
  const changeType = source === 'linkedin' ? 'headline' : subtype;
  const renderedContent = renderSocialDiffMarkdown(item?.metadata || {});

  return (
    <>
      <BadgedAvatar contact={contact} badgeType={source} />
      <TitleAndDate
        title={contact.displayName}
        where={`via ${source}`}
        what={`${changeType} Change`}
        date={date}
        noDate={onlyRelativeDate}
      />
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && item?.metadata.contact}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
      {!expandableDiff && (
        <div className={cn(styles.postContent, styles.notExpandable)}>{renderedContent}</div>
      )}
      {expandableDiff && (
        <div className={styles.postContent}>
          <ExpandableContent renderedContent={renderedContent} activityItemID={item.id} />
        </div>
      )}
    </>
  );
};

const LinkedInAnnouncement = ({ item }: { item: ActivityItem }) => {
  const announcement = item.metadata.linkedInAnnouncement;
  const { company, companyHref, years, jobTitle } = announcement.metadata;
  const $yay = <b>🎉</b>;
  const $name = <b>{item?.metadata.contact.displayName}</b>;

  let $company: ReactNode = company || 'their company';
  if (company && companyHref) {
    $company = (
      <a href={companyHref} target="_blank" onClick={(e) => e.stopPropagation()}>
        {company}
      </a>
    );
  }

  const $duration = years ? `${years} ${pluralize(years, 'year')}` : 'a work anniversary';

  if (announcement.type === 'anniversary') {
    return (
      <>
        {$name} is celebrating {$duration} at {$company} {$yay}
      </>
    );
  }

  if (announcement.type === 'new-job') {
    return (
      <>
        {$name} started a new job{jobTitle ? ` as ${jobTitle}` : ''} at {$company} {$yay}
      </>
    );
  }

  if (announcement.type === 'promotion') {
    return (
      <>
        {$name} was promoted{jobTitle ? ` to ${jobTitle}` : ''} at {$company} {$yay}
      </>
    );
  }

  return <>{renderPostMarkdown(item.metadata.content, true)}</>;
};

const PostActivityItem = ({
  item,
  date,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemComponentProps) => {
  const expandablePost = item?.metadata.postSubtype !== 'announcement';
  const { contact, source, content, media } = item?.metadata || {};

  return (
    <>
      <BadgedAvatar contact={contact} badgeType={source} />
      {expandablePost ? (
        <TitleAndDate
          title={contact.displayName}
          where={`via ${source}`}
          date={date}
          noDate={onlyRelativeDate}
        />
      ) : (
        <div className={styles.postAnnouncement}>
          <LinkedInAnnouncement item={item} />
        </div>
      )}
      <DateDisplay date={date} selected={selected} />
      {selected && (
        <Actions
          noBg={noContactActions}
          activityItemID={item.id}
          contact={!noContactActions && contact}
          noDismissal={noDismissal}
          pinToTop
        />
      )}
      {expandablePost && (
        <div className={styles.postContent}>
          <ExpandableContent
            renderedContent={renderPostMarkdown(content, true)}
            activityItemID={item.id}
          />
          {media?.length > 0 && (
            <div className={styles.imageStack}>
              {media.slice(0, 3).map(({ thumbnail }) => (
                <div
                  key={thumbnail}
                  className={styles.previewImage}
                  style={{ backgroundImage: `url(${thumbnail})` }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const MemorialActivityItem = ({ item, date, selected }: ActivityListItemComponentProps) => {
  const { contact, memorial } = item?.metadata || {};
  let dateDisplay = memorial?.format('MMMM do') || 'Today';

  if (memorial?.isToday) {
    dateDisplay = 'Today';
  } else if (memorial?.isYesterday) {
    dateDisplay = 'Yesterday';
  }

  return (
    <>
      <BadgedAvatar contact={contact} badgeType="memorial" />
      <p>
        {dateDisplay} {dateDisplay === 'Today' ? 'marks' : 'marked'} the anniversary of the passing
        of <b>{contact.displayName}</b>.
      </p>
      <DateDisplay date={date} selected={selected} />
      {selected && <Actions activityItemID={item.id} />}
    </>
  );
};

const subtypeToTitle: Partial<Record<ActivityAnnouncementSubtype, string>> = {
  'intro-video': 'Learning the basics',
  'welcome-message': 'Welcome to Clay',
  'connect-integrations': 'Connect all your accounts',
};

const LibraryLink = ({ metadata }: { metadata: ActivityItemMetadata }) => {
  const [theme] = useTheme();
  const { link, linkTitle, outlet } = metadata;
  let imageSrc = '/img/activity/library.png';

  if (theme === 'light') {
    imageSrc = '/img/activity/library_light.png';
  }

  return <NewsLink title={linkTitle} link={link} imageSrc={imageSrc} outlet={outlet} noVia />;
};

const IntroVideoLink = ({ metadata }: { metadata: ActivityItemMetadata }) => {
  const { link, linkTitle, outlet } = metadata;
  return (
    <NewsLink
      title={linkTitle}
      link={link}
      imageSrc="/img/activity/intro.png"
      outlet={outlet}
      noVia
      noCrop
    />
  );
};

const AnnouncementContent = ({ item }: { item: ActivityItem }) => {
  return (
    <div
      className={cn(
        styles.announcementDetails,
        item?.metadata.subtype === 'intro-video' && styles.introVideoAnnouncement,
      )}
    >
      {item?.metadata.subtype === 'connect-integrations' && <ConnectIntegrations />}
      {renderPostMarkdown(item?.metadata.content)}
      {item?.metadata.subtype === 'library' && <LibraryLink metadata={item.metadata} />}
      {item?.metadata.subtype === 'intro-video' && <IntroVideoLink metadata={item.metadata} />}
    </div>
  );
};

const ConnectIntegrationsBadge = () => (
  <img className={styles.announcementBadge} src="/img/activity/badge-connect-integrations.svg" />
);
const DuplicatesBadge = () => (
  <img className={styles.announcementBadge} src="/img/activity/badge-duplicates.svg" />
);
const GroupsBadge = () => (
  <img className={styles.announcementBadge} src="/img/activity/badge-groups.svg" />
);
const HomeFeedBadge = () => (
  <img className={styles.announcementBadge} src="/img/activity/badge-home-feed.svg" />
);
const IntroVideoBadge = () => (
  <img className={styles.announcementBadge} src="/img/activity/badge-intro-video.svg" />
);
const LibraryBadge = () => (
  <img className={styles.announcementBadge} src="/img/activity/badge-library.svg" />
);

const subtypeToBadge: Partial<Record<ActivityAnnouncementSubtype, () => JSX.Element>> = {
  'connect-integrations': ConnectIntegrationsBadge,
  'duplicates': DuplicatesBadge,
  'groups': GroupsBadge,
  'home-feed': HomeFeedBadge,
  'intro-video': IntroVideoBadge,
  'library': LibraryBadge,
  'welcome-message': ClayBadge,
};

// const subtypeToImage

const AnnouncementActivityItem = ({
  item,
  date,
  selected,
  noDismissal,
}: ActivityListItemComponentProps) => {
  const { user } = useAuthContext();
  const { subtype } = item?.metadata;
  let title = item?.metadata.title || subtypeToTitle[subtype];
  const Badge = subtypeToBadge[subtype] || ClayBadge;

  if (subtype === 'welcome-message' && user?.first_name) {
    title = `${title}, ${user?.first_name}! 🎉`;
  }

  return (
    <>
      <Badge />
      <TitleAndDate title={title} date={date} noDate />
      <DateDisplay date={date} selected={selected} />
      {selected && <Actions activityItemID={item.id} noDismissal={noDismissal} pinToTop noBg />}
      <AnnouncementContent item={item} />
    </>
  );
};

const DuplicatesActivityItem = ({ item, date, selected }: ActivityListItemComponentProps) => {
  const { count } = item?.metadata;

  return (
    <>
      <ClayBadge />
      {count > 0 ? (
        <p>
          Possible duplicates found! Check the{' '}
          <span className={styles.viewLink}>Duplicates view</span> to review and merge.
        </p>
      ) : (
        <p>Scan complete, no duplicates found!</p>
      )}
      <DateDisplay date={date} selected={selected} />
      {selected && <Actions activityItemID={item.id} />}
    </>
  );
};

interface ActivityListItemProps {
  item?: ActivityItem;
  selected: boolean;
  noContactActions?: boolean;
  navigate?: (path: string) => void;
  noDismissal?: boolean;
  onlyRelativeDate?: boolean;
}

export default function ActivityListItem({
  item,
  selected,
  noContactActions,
  noDismissal,
  onlyRelativeDate,
}: ActivityListItemProps) {
  const props: ActivityListItemComponentProps = {
    item,
    date: item.date,
    selected,
    noContactActions: !!noContactActions,
    noDismissal: !!noDismissal,
    onlyRelativeDate: !!onlyRelativeDate,
  };

  switch (item.type) {
    case 'birthday':
      return <BirthdayActivityItem {...props} />;

    case 'memorial':
      return <MemorialActivityItem {...props} />;

    case 'new-member':
      return <NewMemberActivityItem {...props} />;

    case 'shared':
      return <SharedActivityItem {...props} />;

    case 'import':
      return <ImportActivityItem {...props} />;

    case 'reconnect':
      return <ReconnectActivityItem {...props} />;

    case 'reminder':
      return <ReminderActivityItem {...props} />;

    case 'news':
      return <NewsActivityItem {...props} />;

    case 'event':
      return <EventActivityItem {...props} />;

    case 'diff':
      return <DiffActivityItem {...props} />;

    case 'post':
      return <PostActivityItem {...props} />;

    case 'announcement':
      return <AnnouncementActivityItem {...props} />;

    case 'duplicates':
      return <DuplicatesActivityItem {...props} />;

    default:
      return <div>TODO: {item.type}</div>;
  }
}
